import * as React from 'react'

import { Box, Hint, SearchField, Tabs, Text } from '@factorialco/deprecated-design-system'
import { LOCAL_STORAGE_KEYS, LOCAL_STORAGE_KEYS_FACTORIAL_TIPS } from 'config/Vars'
import {
  PerformanceOverviewTargetsDocument,
  PerformanceProcessOverviewTargetsProcessFragment,
} from 'generated/resources/graphql'

import SingleChoice from 'modules/core/components/Filter/filters/SingleChoice'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import i18n from 'modules/core/lib/i18n'
import { matchText } from 'modules/core/lib/stringUtils'
import useLocalStorage from 'modules/core/lib/useLocalStorage'

import { EmptyState } from 'modules/performance_process/components/insights/Answers/common/EmptyState'
import { TeamInsights } from 'modules/performance_process/components/insights/TeamInsights'
import ProcessTargetDataTable from 'modules/performance_process/components/overview/ProcessTargetDataTable'
import SimpleChip from 'modules/performance_process/components/overview/Targets/SimpleChip'

import { Analytics } from './Analytics'
import Answers from './Answers'
import { FinalEmployeeScoresInsights } from './EmployeeScore/Insights'
import Heatmap from './Heatmap'
import { TABS_IDS, buildTabs } from './buildTabs'

export const OverviewTargets: React.FC<{
  review: PerformanceProcessOverviewTargetsProcessFragment
  hasFullVisibility: boolean
  hideManagerFilter?: boolean
  defaultManagerFilterForEmployeeId?: string
  showTeamInsights?: boolean
  showEmployeeScores?: boolean
}> = ({
  review,
  hasFullVisibility,
  hideManagerFilter = false,
  defaultManagerFilterForEmployeeId,
  showTeamInsights = true,
  showEmployeeScores = false,
}) => {
  const { employee: currentEmployee, access: currentAccess } = useCurrentSession()

  const [tabs, setTabs] = React.useState(buildTabs(showTeamInsights, showEmployeeScores))
  const [preSelectedManagerId, setPreSelectedManagerId] = useLocalStorage<[string | null, string]>(
    LOCAL_STORAGE_KEYS.preSelectedManagerId
  )

  const previouslySelectedManagerId =
    preSelectedManagerId && review.id === preSelectedManagerId[1] ? preSelectedManagerId[0] : null
  const initialManagerFilter = hideManagerFilter
    ? defaultManagerFilterForEmployeeId || null
    : previouslySelectedManagerId || defaultManagerFilterForEmployeeId || null
  const [selectedManagerId, setSelectedManagerId] = React.useState<string | null>(
    initialManagerFilter
  )
  const [onlyDirectReports, setOnlyDirectReports] = React.useState<boolean>(true)
  const { search, debouncedSearch, onSearch } = useDebouncedSearch()

  // Boolean to hide direct/indirect SimpleChip in case of team insights tab selection as insights always take into account all reports
  const isTeamTabActive = tabs.some((tab) => tab.active && tab.id === TABS_IDS.team)

  const showManagerFilter =
    !hideManagerFilter && (review.targetManager.length > 0 || hasFullVisibility)

  const onTabClicked = (tabId: string) => {
    setTabs(
      tabs.map((tab) => {
        if (tab.id === tabId) {
          return {
            ...tab,
            active: true,
          }
        }
        return {
          ...tab,
          active: false,
        }
      })
    )
  }

  const options =
    review?.targetManager?.map((m) => ({
      label: m.managerFullName,
      value: String(m.id),
    })) ?? []

  const optionsToShow = search
    ? options.filter((option) => matchText(debouncedSearch, option.label))
    : options

  const selectedOption = options.filter((option) => option.value === selectedManagerId)

  const managerSelectedWhenClearedFilter = hasFullVisibility
    ? null
    : (defaultManagerFilterForEmployeeId ?? null)
  const changeManagerFilter = (value: string | null | undefined) => {
    setSelectedManagerId(value || managerSelectedWhenClearedFilter)
    setPreSelectedManagerId([value || managerSelectedWhenClearedFilter, review.id])
    setOnlyDirectReports(true)
  }

  const { data } = useQuery(PerformanceOverviewTargetsDocument, {
    skip: !selectedManagerId,
    variables: {
      managerId: String(selectedManagerId),
    },
  })

  const selectedManager = review?.targetManager?.find((m) => selectedManagerId === String(m.id))
  const directReporteesCount = data?.employees?.directReportees?.totalCount ?? 0
  const reporteesCount = data?.employees?.reportees?.totalCount ?? 0
  const hasDirectAndIndirectReportees = reporteesCount > directReporteesCount

  const managedByFilter = selectedManagerId
    ? { managerEmployeeId: selectedManagerId, onlyDirectReports: onlyDirectReports }
    : hasFullVisibility || !currentEmployee
      ? undefined
      : { managerEmployeeId: currentEmployee.id, onlyDirectReports: onlyDirectReports }

  const [managerFilterHintIsHidden, setManagerFilterHintIsHidden] = useLocalStorage<boolean>(
    LOCAL_STORAGE_KEYS_FACTORIAL_TIPS.hidePerformanceProcessManagerFilterHint
  )

  const handleHintClose = () => {
    setManagerFilterHintIsHidden(true)
  }

  return (
    <Box gap="s16">
      <Box gap="s48">
        <Text.Header2 weight="semibold">
          {i18n.t('performance_process.overview.results.title')}
        </Text.Header2>
      </Box>
      <Box gap="s16">
        <Box paddingBottom="s24">
          <Box position="relative" width="full">
            {showManagerFilter && (
              <Box flexDirection="row" gap="s8" paddingBottom="s16">
                <Hint.Popover.Provider
                  steps={1}
                  isHidden={!!managerFilterHintIsHidden}
                  onClose={handleHintClose}
                  onFinish={handleHintClose}
                >
                  <Hint.Popover
                    step={1}
                    title={i18n.t('performance_process.overview.results.manager_filter_hint.title')}
                    description={i18n.t(
                      'performance_process.overview.results.manager_filter_hint.hint_description'
                    )}
                    actionLabel={i18n.t(
                      'performance_process.overview.results.manager_filter_hint.action'
                    )}
                    position="bottom right"
                  >
                    <SingleChoice
                      header={
                        <Box padding="s4">
                          <SearchField
                            search={search}
                            onSearch={onSearch}
                            variant="big"
                            placeholder=""
                          />
                        </Box>
                      }
                      label={i18n.t('performance_process.overview.results.manager_search_label')}
                      value={selectedManagerId}
                      onValueChange={(value) => changeManagerFilter(value || null)}
                      options={
                        optionsToShow.length > 0
                          ? optionsToShow
                          : [
                              ...selectedOption,
                              {
                                label: i18n.t(
                                  'performance_process.overview.results.manager_search_emtpy'
                                ),
                                value: null,
                              },
                            ]
                      }
                    />
                  </Hint.Popover>
                </Hint.Popover.Provider>

                {selectedManagerId && !isTeamTabActive && (
                  <Box flexDirection="row" gap="s8" alignItems="center">
                    <Text.Normal size="200">
                      {i18n.t('surveys.review.dashboard.filters.reportees')}
                    </Text.Normal>
                    <SimpleChip
                      label={i18n.t('surveys.review.dashboard.filters.direct')}
                      active={onlyDirectReports}
                      disabled={false}
                      onClick={() => setOnlyDirectReports(true)}
                    />
                    <SimpleChip
                      label={i18n.t('surveys.review.dashboard.filters.direct_and_indirect')}
                      active={!onlyDirectReports}
                      disabled={!hasDirectAndIndirectReportees}
                      onClick={() => setOnlyDirectReports(false)}
                    />
                  </Box>
                )}
              </Box>
            )}
            <Box width="full">
              <Tabs
                key={`tabs-menu-jobs-requisitions`}
                currentId={tabs.find((tab) => tab.active)?.id ?? ''}
                onTabClicked={(tabId) => onTabClicked(tabId)}
                hasAdditionalPadding
              >
                {tabs.map((tab) => (
                  <Tabs.Tab key={tab.id} id={tab.id} label={tab.label} />
                ))}
              </Tabs>
            </Box>
          </Box>
          <Box paddingTop="s40">
            {tabs.map((tab) => (
              <>
                {tab.id === TABS_IDS.participation && tab.active && review && (
                  <ProcessTargetDataTable
                    managedByFilter={managedByFilter}
                    reviewId={review.id}
                    agreementEnabled={review.agreementsConfiguration.enabled}
                    showEmployeeScores={showEmployeeScores}
                    reviewerStrategies={review.reviewerStrategies ?? []}
                  />
                )}
                {tab.id === TABS_IDS.answers && tab.active && (
                  <Answers reviewId={review.id} managedByFilter={managedByFilter} />
                )}
                {tab.id === TABS_IDS.heatmap && tab.active && (
                  <Heatmap reviewId={review.id} managedByFilter={managedByFilter} />
                )}
                {tab.id === TABS_IDS.analytics && tab.active && (
                  <Analytics reviewId={review.id} managedByFilter={managedByFilter} />
                )}
                {tab.id === TABS_IDS.employee_scores && tab.active && (
                  <FinalEmployeeScoresInsights
                    reviewId={review.id}
                    managedByFilter={managedByFilter}
                  />
                )}
                {tab.id === TABS_IDS.team &&
                  tab.active &&
                  (showManagerFilter && !selectedManagerId ? (
                    <EmptyState
                      title={i18n.t('performance_process.insights.answers.empty_state_all_answers')}
                    />
                  ) : (
                    <TeamInsights
                      reviewId={review.id}
                      forAccessId={selectedManager?.managerAccessId || currentAccess.id}
                    />
                  ))}
              </>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
